// src/scene/textures.ts
import * as THREE from 'three';
import gsap from 'gsap';
import type { WallHandle } from './wall';

const loader = new THREE.TextureLoader();
const LOAD_DIST = 22;        // load works within this many world units of the camera
const INTERVAL_MS = 200;
let lastCheck = 0;
const MAX_RETRIES = 3;
const failures = new Map<string, number>();
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
let pulsePhase = 0;

export function updateLoadingPulse(wall: WallHandle, deltaS: number): void {
  if (reducedMotion) return;
  pulsePhase += deltaS;
  const opacity = 0.55 + 0.15 * Math.sin(pulsePhase * 4);
  for (const plane of wall.planes) {
    if (plane.state !== 'loading') continue;
    plane.mesh.material.opacity = opacity;
  }
}

export function updateLazyTextures(wall: WallHandle, camera: THREE.PerspectiveCamera): void {
  const now = performance.now();
  if (now - lastCheck < INTERVAL_MS) return;
  lastCheck = now;

  for (const plane of wall.planes) {
    if (plane.state !== 'empty') continue;
    if (plane.position.distanceTo(camera.position) > LOAD_DIST) continue;
    if ((failures.get(plane.work.id) ?? 0) >= MAX_RETRIES) continue;
    plane.state = 'loading';
    plane.mesh.material.opacity = 0.55;
    loader.load(
      `${import.meta.env.BASE_URL}textures/${plane.work.id}-s.webp`,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        const mat = plane.mesh.material;
        mat.opacity = 0;
        mat.map = tex;
        mat.color.set('#ffffff');
        mat.needsUpdate = true;
        plane.state = 'ready';
        gsap.to(mat, { opacity: 1, duration: 0.6, ease: 'power1.out' });
      },
      undefined,
      () => {
        failures.set(plane.work.id, (failures.get(plane.work.id) ?? 0) + 1);
        plane.state = 'empty';
      },
    );
  }
}
